import React, { useEffect } from "react";
import BaseModal from "@reusables/Modals/BaseModal";
import { useTranslation } from "react-i18next";
import BaseButton from "@reusables/BaseButton";
import UIButton from "@reusables/UIButton";
import {
    useReplaceTripletexTokenMutation,
    useUpdateTripletexConfigurationMutation
} from "@redux/features/integrations/integrationsApi";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import BaseSwitch from "@reusables/BaseSwitch";
import BaseInput from "@reusables/BaseInput";
import { cn, toastError } from "@helpers/utils";
import BaseHelp from "@reusables/BaseHelp";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { Integration } from "@/types/general";
import { enableIntegration } from "@redux/features/auth/authSlice";
import { toast } from "react-toastify";
import SettingsSection
    from "@components/Dashboard/pages/SettingsPage/Integrations/ThirdPartyIntegrations/components/SettingsSection";

interface PowerOfficeSettingsModal {
    isOpen: boolean;
    onClose: () => void;
}

const formSchema = z.object({
    projects_support: z.boolean(),
    auto_invoicing: z.boolean()
});

type FormTyping = z.infer<typeof formSchema>;

const tokenFormSchema = z.object({
    employee_token: z.string().trim().nonempty()
});

type TokenFormTyping = z.infer<typeof tokenFormSchema>;

export default function TripletexSettingsModal({ isOpen, onClose }: PowerOfficeSettingsModal) {
    const { t } = useTranslation("", { keyPrefix: "imports.modules.tripletex.settingsModal" });

    const [updateConfiguration, { isLoading: isConfigurationUpdateLoading }] = useUpdateTripletexConfigurationMutation();
    const [replaceToken, { isLoading: isTokenReplacementLoading }] = useReplaceTripletexTokenMutation();

    const dispatch = useAppDispatch();

    const form = useForm<FormTyping>({
        defaultValues: {
            projects_support: false,
            auto_invoicing: true
        }
    });

    const tokenForm = useForm<TokenFormTyping>({
        resolver: zodResolver(tokenFormSchema),
        defaultValues: {
            employee_token: ""
        }
    });

    const tripletexIntegrationSettings = useAppSelector(state => state.auth.user?.company?.integrations?.tripletex);

    const onSubmit = form.handleSubmit((data) => {
        updateConfiguration({
            projects_support: data.projects_support,
            auto_invoicing: data.auto_invoicing
        })
            .unwrap()
            .then((data) => {
                dispatch(enableIntegration(data));
                toast.success(t("responses.saved"));
            })
            .catch(err => {
                console.error(err);
                toastError(err);
            });
    });

    const onTokenSubmit = tokenForm.handleSubmit((data) => {
        replaceToken({
            employee_token: data.employee_token
        })
            .unwrap()
            .then((data) => {
                dispatch(enableIntegration(data));
                toast.success(t("token.responses.success"));
                tokenForm.reset();
            })
            .catch(err => {
                console.error(err);
                toastError(err);
            });
    }, () => {
        // Whitespace-only input passes the native "required" check, but not zod's. The error popper
        // is rendered only while the field is focused, hence the manual focus.
        tokenForm.setFocus("employee_token");
    });

    useEffect(() => {
        if (tripletexIntegrationSettings) {
            if ("projects_support" in tripletexIntegrationSettings) {
                form.setValue("projects_support", (tripletexIntegrationSettings as Integration.ActiveState.Tripletex).projects_support);
            }

            if ("auto_invoicing" in tripletexIntegrationSettings) {
                form.setValue("auto_invoicing", (tripletexIntegrationSettings as Integration.ActiveState.Tripletex).auto_invoicing);
            }
        }
    }, [tripletexIntegrationSettings]);

    // Modal's children stay mounted, so we drop the typed token whenever the modal is opened or closed
    useEffect(() => {
        tokenForm.reset();
    }, [isOpen]);

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            width={800}
            padding="45px"
            useCloseIcon
            position={"center"}
        >
            <div className="text-xl font-semibold text-center text-accent mb-4">
                {t("heading")}
            </div>

            <div className={"space-y-4"}>
                {/* ## Features ## */}
                <form onSubmit={onSubmit}>
                    <SettingsSection
                        label={t("features")}
                        action={
                            <UIButton size="md" type="submit" loading={isConfigurationUpdateLoading}>
                                {t("buttons.save")}
                            </UIButton>
                        }
                    >
                        <Controller
                            control={form.control}
                            name="projects_support"
                            render={({ field }) => (
                                <div
                                    className={cn(
                                        `flex flex-row items-center justify-between grow border-[1px] border-solid rounded-lg bg-white transition-all overflow-hidden relative`,
                                        `border-inputs-border-default`,
                                        "hover:border-inputs-border-focused",
                                        "p-3 cursor-pointer",
                                        field.value && "border-inputs-border-focused"
                                    )}
                                    onClick={() => field.onChange(!field.value)}
                                >
                                    <div className={"flex items-center gap-2"}>
                                        <div>{t("projectsSupport.label")}</div>
                                        <BaseHelp value={t("projectsSupport.tip")} />
                                    </div>

                                    <BaseSwitch
                                        checked={field.value}
                                    />
                                </div>
                            )}
                        />

                        <Controller
                            control={form.control}
                            name="auto_invoicing"
                            render={({ field }) => (
                                <div
                                    className={cn(
                                        `flex flex-row items-center justify-between grow border-[1px] border-solid rounded-lg bg-white transition-all overflow-hidden relative`,
                                        `border-inputs-border-default`,
                                        "hover:border-inputs-border-focused",
                                        "p-3 cursor-pointer",
                                        field.value && "border-inputs-border-focused"
                                    )}
                                    onClick={() => field.onChange(!field.value)}
                                >
                                    <div className={"flex items-center gap-2"}>
                                        <div>{t("autoInvoicing.label")}</div>
                                        <BaseHelp value={t("autoInvoicing.tip")} />
                                    </div>

                                    <BaseSwitch
                                        checked={field.value}
                                    />
                                </div>
                            )}
                        />
                    </SettingsSection>
                </form>

                {/* ## Employee token ## */}
                <form onSubmit={onTokenSubmit}>
                    <SettingsSection
                        label={t("token.label")}
                        tip={t("token.tip")}
                        action={
                            <UIButton size="md" type="submit" loading={isTokenReplacementLoading}>
                                {t("token.button")}
                            </UIButton>
                        }
                    >
                        <Controller
                            control={tokenForm.control}
                            name="employee_token"
                            render={({ field, fieldState }) => (
                                <BaseInput
                                    {...field}
                                    error={fieldState.error}
                                    label={t("token.input.label")}
                                    placeholder={t("token.input.placeholder")}
                                    required
                                />
                            )}
                        />
                    </SettingsSection>
                </form>

                <BaseButton
                    buttonWidth="100%"
                    text={t("buttons.close")}
                    size="md"
                    type="button"
                    primaryOutlined
                    onClick={onClose}
                />
            </div>
        </BaseModal>
    );
}
